/**
 * Method section setup: section 02 of the controls sidebar.
 *
 * Builds the FAMILY (A / B / C) and ALGORITHM (Family C only) select rows
 * and attaches their change listeners. The returned refs are consumed by
 * the parent createControls closure in:
 *   - syncVisibility:  algo.row visibility gated on family C
 *   - syncSliderValues: family.select / algo.select programmatic sync
 *   - syncI18n:         option-text refresh on locale change
 *
 * Extracted from createControls (CSS-04.2): public API of createControls
 * unchanged; no behavioural change.
 */

import type { FamilyAAlgorithm, Store } from "../../state";
import { createDefaultPatternImage } from "../../state";
import type { MethodPartial } from "../../util/method-patch";

// ---------------------------------------------------------------------------
// Inline type aliases matching the private interfaces in controls.ts.
// ---------------------------------------------------------------------------

interface SectionGroup {
  section: HTMLElement;
  body: HTMLDivElement;
  head: HTMLHeadingElement;
}

interface SelectRow {
  row: HTMLDivElement;
  select: HTMLSelectElement;
  root: HTMLDivElement;
  refresh: () => void;
}

interface SelectRowOptions<T extends string> {
  labelKey: string;
  tipKey?: string;
  ctrlId: string;
  options: ReadonlyArray<readonly [T, string]>;
  value: T;
}

/** Dependencies injected by createControls so this module can use the
 *  private helper functions without duplicating their implementations. */
export interface MethodSectionDeps {
  store: Store;
  /** The `.controls` aside element; the section is appended here. */
  root: HTMLElement;
  makeSection: (
    ix: string,
    titleKey: string,
    variant?: "" | "violet" | "yellow",
  ) => SectionGroup;
  buildSelectRow: <T extends string>(opts: SelectRowOptions<T>) => SelectRow;
  patchMethod: (partial: MethodPartial) => void;
  createFamilyStarterPatch: (family: "A" | "B" | "C") => MethodPartial;
}

// ---------------------------------------------------------------------------
// Refs returned to the parent closure
// ---------------------------------------------------------------------------

/** Handles that syncVisibility / syncSliderValues / syncI18n need from the
 *  method section. Both are plain SelectRow objects as built by the private
 *  buildSelectRow helper in controls.ts. */
export interface MethodSectionRefs {
  /** Family picker (A / B / C). */
  family: SelectRow;
  /** Algorithm picker (Family C only; hidden for A and B). */
  algo: SelectRow;
}

// ---------------------------------------------------------------------------
// Setup function
// ---------------------------------------------------------------------------

/**
 * Builds and attaches the METHOD section (section 02) to `deps.root`.
 * Registers change listeners that route through `deps.patchMethod`.
 * Returns the SelectRow refs required by the parent closure.
 */
export function setupMethodSection(deps: MethodSectionDeps): MethodSectionRefs {
  const {
    store,
    root,
    makeSection,
    buildSelectRow,
    patchMethod,
    createFamilyStarterPatch,
  } = deps;

  // ========== 02 METHOD - yellow badge per I1 ==========
  const methodSection = makeSection("02", "label.method", "yellow");
  root.appendChild(methodSection.section);

  const family = buildSelectRow({
    labelKey: "label.family",
    tipKey: "tip.family",
    ctrlId: "ctrl-family",
    options: [
      ["A", "method.family.a"],
      ["B", "method.family.b"],
      ["C", "method.family.c"],
    ] as const,
    value: store.get().family,
  });
  const familyLabelWrap = family.row.querySelector<HTMLElement>(".row-label");
  const familyTip = familyLabelWrap?.querySelector<HTMLElement>(".info-btn");
  family.row.classList.add("row-select-inline-help");
  familyLabelWrap?.classList.add("row-label--sr-only");
  if (familyTip) family.root.after(familyTip);
  methodSection.body.appendChild(family.row);

  const algo = buildSelectRow({
    labelKey: "label.algorithm",
    tipKey: "tip.algorithm",
    ctrlId: "ctrl-algorithm",
    options: [
      ["bayer", "algorithm.bayer"],
      ["bluenoise", "algorithm.bluenoise"],
      ["floyd-steinberg", "algorithm.fs"],
      ["atkinson", "algorithm.atkinson"],
      ["werness", "algorithm.werness"],
    ] as const,
    value: store.get().algorithmA,
  });
  methodSection.body.appendChild(algo.row);

  family.select.addEventListener("change", () => {
    const nextFamily = family.select.value as "A" | "B" | "C";
    patchMethod(createFamilyStarterPatch(nextFamily));
    if (nextFamily === "B" && store.get().patternImage === null) {
      store.patch({ patternImage: createDefaultPatternImage() });
    }
  });
  algo.select.addEventListener("change", () => {
    patchMethod({ algorithmA: algo.select.value as FamilyAAlgorithm });
  });

  return { family, algo };
}
